import cn from 'clsx'
import React from 'react'
import { useDrop } from 'react-dnd'

import { BRIEF } from '@disco/constants/ItemTypes'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import {
  getMovingBriefs,
  moveBriefsToBriefProject,
} from '@disco/state-management/reducers/entities/briefs'
import { getExplorerPanes } from '@disco/state-management/selectors/explorer'
import {
  closePane,
  updatePanes,
} from '@disco/state-management/actions/ExplorerActions'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'

import type { Brief, BriefProjectId } from '@disco/types/types/brief'
import type { ExplorerPane } from '@disco/types/types/explorer'
import type { SelectableItem } from '@disco/types/types/selection'

import { useDispatch, useSelector } from 'hooks/use-redux'
import type { AppDispatch } from 'store/configureStore'

type Project = {
  briefProjectId?: BriefProjectId
  briefProjectName?: string
} | null

type DraggedBrief = { brief?: Brief } | null | undefined

type BriefDropParams = {
  dispatch: AppDispatch
  selectedItems?: SelectableItem[]
  pane?: ExplorerPane
  movingBriefs: number[]
  panes: ExplorerPane[]
  project: Project
}

export const handleMoveBriefsToBriefProject = (
  draggedItem: DraggedBrief,
  {
    dispatch,
    selectedItems,
    pane,
    movingBriefs,
    panes,
    project,
  }: BriefDropParams,
) => {
  if (!draggedItem?.brief || !dispatch) {
    return
  }

  const toProjectId: BriefProjectId | null = project?.briefProjectId ?? null
  const projectName = project?.briefProjectName
  const fromProjectId = draggedItem.brief.project?.id ?? null

  const multipleBriefsSelected =
    (selectedItems?.length ?? 0) > 0 &&
    isDraggedItemPartOfSelection(selectedItems ?? [], draggedItem)

  let briefsToMove: Brief[] = []

  if (!multipleBriefsSelected) {
    briefsToMove.push({
      ...draggedItem.brief,
      project: toProjectId ? { id: toProjectId, name: projectName } : null,
    })
  } else {
    briefsToMove = (selectedItems ?? []).map(item => ({
      ...(item as Brief),
      project: toProjectId ? { id: toProjectId, name: projectName } : null,
    }))
  }

  const briefIds = briefsToMove.map(brief => brief.id)
  const alreadyMoving = movingBriefs.some(briefId => briefIds.includes(briefId))
  if (alreadyMoving) {
    return
  }

  const updatedPanes = [...panes] as ExplorerPane[]

  if (pane && (toProjectId === null || (toProjectId && pane.index !== 0))) {
    updatedPanes[pane.index] = {
      ...pane,
      results: [...briefsToMove, ...pane.results],
    }
  }

  if (toProjectId && pane?.index === 0) {
    const targetProjectPane = updatedPanes.find(
      p => p.meta?.briefProjectId === toProjectId,
    )
    if (targetProjectPane) {
      updatedPanes[targetProjectPane.index] = {
        ...targetProjectPane,
        results: [...briefsToMove, ...targetProjectPane.results],
      }
    }
  }

  const sourcePane = fromProjectId
    ? updatedPanes.find(p => p.meta?.briefProjectId === fromProjectId)
    : updatedPanes[0]

  if (sourcePane) {
    updatedPanes[sourcePane.index] = {
      ...sourcePane,
      results: sourcePane.results.filter(
        brief => !briefsToMove.some(moved => moved.id === brief.id),
      ),
    } as ExplorerPane
  }

  const briefDetailsPane = updatedPanes[2]
  let shouldClosePane = false

  if (
    briefDetailsPane &&
    'meta' in briefDetailsPane &&
    briefDetailsPane.meta?.briefId
  ) {
    const openBriefId = briefDetailsPane.meta.briefId
    const isOpenBriefBeingMoved = briefsToMove.some(
      moved => moved.id === openBriefId,
    )
    if (isOpenBriefBeingMoved) {
      shouldClosePane = true
      if (!__REACT_NATIVE__ && fromProjectId) {
        window.history.pushState({}, '', `/briefs/projects/${fromProjectId}`)
      }
    }
  }

  if (shouldClosePane) {
    dispatch(closePane(2))
  }

  const finalPanes = shouldClosePane ? updatedPanes.slice(0, 2) : updatedPanes
  dispatch(updatePanes(finalPanes))
  dispatch(
    moveBriefsToBriefProject({
      briefs: briefsToMove,
      toProjectId,
      fromProjectId,
      pane,
    }),
  )
}

type Props = {
  children?: React.ReactNode
  project: Project
  selectedItems?: SelectableItem[]
  pane?: ExplorerPane
}

export default function BriefDropWrapper({
  children,
  project,
  selectedItems,
  pane,
}: Props) {
  const dispatch = useDispatch()
  const movingBriefs = useSelector(getMovingBriefs)
  const panes = useSelector(getExplorerPanes)

  const [{ isOver, canDrop }, dropRef] = useDrop<
    DraggedBrief,
    unknown,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: [BRIEF],
      canDrop: (item, monitor) => {
        const isShallowOver = monitor.isOver({ shallow: true })
        if (!isShallowOver || !item?.brief) {
          return false
        }
        return project?.briefProjectId
          ? item.brief.project?.id !== project.briefProjectId
          : item.brief.project !== null
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) {
          return
        }
        handleMoveBriefsToBriefProject(item, {
          dispatch,
          selectedItems,
          pane,
          movingBriefs,
          panes,
          project,
        })
      },
      collect: monitor => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [dispatch, selectedItems, pane, movingBriefs, panes, project],
  )

  return (
    <div
      ref={dropRef}
      className={cn({ HighlightedDropTarget: isOver && canDrop })}
    >
      {children}
    </div>
  )
}
